import { ActionsHook } from "@/lib/Components/Actions/ActionGroupButtons";
import {
  KeyIcon,
  BuildingOffice2Icon,
  TrashIcon,
} from "@heroicons/react/20/solid";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { ResetPasswordDialog } from "@/app/Users/Components/ResetPasswordDialog";
import { AddUserToOrganisationDialog } from "@/app/Users/Components/AddUserToOrganisationDialog";
import { useConfirmDeleteDialog } from "@/lib/Components/Dialog/Hooks/useConfirmDeleteDialog";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { deleteUserMutation } from "@/app/Users/GraphQL/userMutations";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";

type UserActionModel = {
  id: string;
  name?: string | null;
};

export function useUserActions<T extends UserActionModel>(): ActionsHook<T> {
  const { isAdmin } = useTenant();
  const { open: openResetPassword } = useDialog(ResetPasswordDialog);
  const { open: openAddToOrg } = useDialog(AddUserToOrganisationDialog);
  const { open: openConfirmDelete } = useConfirmDeleteDialog();
  const { mutateAsync: deleteUser } = useGqlMutation(deleteUserMutation);
  const navigate = useNavigate();

  return (user) => {
    if (!isAdmin) {
      return [];
    }

    return [
      {
        label: "Actions",
        items: [
          {
            actions: [
              {
                label: "Add to organisation",
                Icon: BuildingOffice2Icon,
                isAdminAction: true,
                handleAction: () => {
                  openAddToOrg({ userId: user.id });
                },
              },
            ],
          },
          {
            actions: [
              {
                label: "Reset password",
                Icon: KeyIcon,
                isAdminAction: true,
                handleAction: () => {
                  openResetPassword({
                    userId: user.id,
                    userName: user.name ?? null,
                  });
                },
              },
            ],
          },
          {
            actions: [
              {
                label: "Delete",
                Icon: TrashIcon,
                color: "red",
                isAdminAction: true,
                handleAction: () => {
                  openConfirmDelete({
                    title: `Delete ${user.name ?? "user"}`,
                    message:
                      "Are you sure you want to delete this user? Their profile, waitlists and alerts are deleted permanently, and their bookings are unlinked from them. This cannot be undone.",
                    buttonText: "Delete user",
                    onDelete: async () => {
                      await deleteUser({ id: user.id });
                      toast.success("User deleted");
                      await navigate({
                        from: "/$tenant/$orgId/supplier/{-$supplierId}",
                        to: "/$tenant/$orgId/supplier/{-$supplierId}/users",
                        params: (prev) => prev,
                      });
                    },
                  });
                },
              },
            ],
          },
        ],
      },
    ];
  };
}
